import { OPENID_EVENT, AppSettings, eventBus, openid } from '@app/base';
import { Alert } from 'antd';
import { OidcMetadata, SignoutRedirectArgs, WebStorageStateStore } from 'oidc-client-ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthProvider, hasAuthParams, useAuth } from 'react-oidc-context';
import { User } from 'oidc-client-ts';
import { useSpin } from '@app/ui';
import { unsubscribeWispoPushNotifications } from '@app/notifications/api';

export interface AuthenticationProviderProps {
  Component: React.ComponentType;
}

const SIGN_IN_REQUIRED_ERRORS = [
  // as per https://openid.net/specs/openid-connect-core-1_0.html#AuthError
  'interaction_required',
  'login_required',
  'account_selection_required',
  'consent_required',
];

interface State {
  targetPath: string;
}

const isState = (obj: any): obj is State => {
  return obj && typeof obj.targetPath === 'string';
};

export function AuthenticationProvider(props: AuthenticationProviderProps) {
  const storage = useRef(new WebStorageStateStore({ store: window.localStorage }));
  const [metadata, setMetadata] = useState<OidcMetadata>();

  useEffect(() => {
    openid.getOpenIdConfiguration().then(setMetadata);
  }, []);

  const onSigninCallback = useCallback((user: User | void) => {
    const state = user?.state;

    if (isState(state)) {
      const redirect = state.targetPath;
      window.history.replaceState({}, document.title, redirect);
    } else {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (metadata == null) {
    return null;
  }

  return (
    <AuthProvider
      metadata={metadata}
      authority={`${AppSettings.identityServerUrl}/`}
      client_id="webapp"
      redirect_uri={window.location.origin}
      silent_redirect_uri={`${window.location.origin}/silent-renew.html`}
      scope="profile app-api id.public"
      revokeTokensOnSignout
      userStore={storage.current}
      onSigninCallback={onSigninCallback}
    >
      <Authenticate {...props} />
    </AuthProvider>
  );
}

function Authenticate(props: AuthenticationProviderProps) {
  const { Component } = props;
  const { isAuthenticated, activeNavigator, isLoading, error } = useAuth();
  const { signinRedirect, signoutRedirect, signinSilent } = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [isSilentSigninInProgress, setSilentSigninInProgress] = useState(false);
  const [isSigningOut, setSigningOut] = useState(false);

  useSpin(isLoading && !isSilentSigninInProgress);
  useSpin(isSigningOut || !isAuthenticated);

  useEffect(() => {
    if (!hasAuthParams() && !isAuthenticated && !activeNavigator && !isLoading && !hasTriedSignin) {
      signinRedirect({ state: { targetPath: window.location.href } });
      setHasTriedSignin(true);
    }
  }, [isAuthenticated, activeNavigator, isLoading, hasTriedSignin, signinRedirect]);

  const onStorageEvent = useCallback((event: StorageEvent) => {
    event.key === 'openid:signout' && signoutRedirect();

    // eslint-disable-next-line
  }, []);

  const signOut = useCallback(async (args?: SignoutRedirectArgs) => {
    await unsubscribeWispoPushNotifications();
    setSigningOut(true);
    signoutRedirect(args);
    window.removeEventListener('storage', onStorageEvent);
    localStorage.setItem('openid:signout', Date.now().toString());

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.addEventListener('storage', onStorageEvent);
  }, [onStorageEvent]);

  useEffect(() => {
    return eventBus.subscribe(OPENID_EVENT.SIGNOUT, signOut);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return eventBus.subscribe(OPENID_EVENT.SIGNOUT_AUTO, () =>
      signOut({ extraQueryParams: { autoLogout: true } }),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return eventBus.subscribe(OPENID_EVENT.RENEW_REVOKED, (resolve: () => void) => {
      setSilentSigninInProgress(true);
      signinSilent({})
        .then((user) => {
          if (!user) signOut({ extraQueryParams: { revoked: true } });
          else resolve();
        })
        .finally(() => setSilentSigninInProgress(false));
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error && SIGN_IN_REQUIRED_ERRORS.includes((error as any).error) && !isSilentSigninInProgress) {
      signOut();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading && !isSilentSigninInProgress) {
    return null;
  }

  if (error) {
    // TODO: its temporary solution, will be updated
    signinSilent();
    return (
      <div className="flex justify-center">
        <Alert className="w-1/2 mt-3" type="error" banner message="Oops.." description={error.message} />
      </div>
    );
  }

  if (isSigningOut || !isAuthenticated) {
    return null;
  }

  return <Component />;
}
